import { AxiosError } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import React from 'react'
import useSWR from 'swr'
import { RolodexFileRef } from 'types'
interface RolodexFileLSQueryBody {
  /** The ID of the entry to list files under */
  entry_id: string
  /** Path filtering docs on how to use https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/4703354928/Moving+From+files+list+to+files+ls */
  path?: string
  /** Limit the number of files returned */
  limit?: number
}

// -------------------------------------------------------------

export function useGetRolodexFileLSFetcher(): (
  query: RolodexFileLSQueryBody
) => Promise<ReadonlyArray<RolodexFileRef>> {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = React.useContext(RolodexContext)

  return React.useCallback(
    (query) => {
      const body = JSON.stringify(query)

      return new Promise((resolve, reject) => {
        if (!isAuthenticated) {
          reject(new Error('User is not authenticated'))
          return
        }
        return axios
          .post(`${apiURL}/api/v2/files/ls`, body, {
            headers: { 'Content-Type': 'application/json' },
          })
          .then((res) => resolve(res.data))
          .catch((err) => reject(err))
      })
    },
    [apiURL, axios, isAuthenticated]
  )
}

// -------------------------------------------------------------

export const useRolodexFileLS = (
  query: RolodexFileLSQueryBody | undefined
): {
  /** An array of Rolodex file refs, useful for later loading */
  fileRefs: ReadonlyArray<RolodexFileRef>
  /** Whether the SWR query is still loading */
  isLoading: boolean
  /** Whether the SWR query is still validating */
  isValidating: boolean
  /** To account for the initial case before the query fires  */
  hasEverLoaded: boolean
  /** Any errors returned from SWR */
  error: AxiosError
  /** A callback for forcing a re-fetch */
  forceRefresh: () => Promise<void>
} => {
  //----------------------------------------------------------------------------

  const fetcherBase = useGetRolodexFileLSFetcher()
  const fetcher = React.useMemo(() => {
    if (!query) {
      return () => Promise.resolve([])
    }
    return () => fetcherBase(query)
  }, [fetcherBase, query])

  const { data, isLoading, isValidating, error, mutate } = useSWR(
    JSON.stringify(query), // KEY
    fetcher
  )
  const fileRefs = React.useMemo(() => data?.flat() ?? [], [data])

  const forceRefresh = React.useCallback(
    async () => await mutate().then(() => {}),
    [mutate]
  )

  return {
    fileRefs,
    isLoading,
    isValidating,
    error,
    hasEverLoaded: !!data,
    forceRefresh,
  }
}
